import { useDroppable } from '@dnd-kit/core';
import type { ColunaCards, EtapaPipeline, PrazoCard } from '../types';
import { CardPrazo } from './CardPrazo';
import { useMe } from '../hooks';

/**
 * Esteira de "Em andamento" — sub-colunas (lanes) por etapa do pipeline DOCAS.
 * As lanes são REFLEXO do backend (etapa_pipeline): o card "anda" sozinho da
 * esquerda p/ a direita conforme o pipeline avança (polling). Não são alvos de
 * drag — o drag macro (entrar/sair de "Em andamento") usa o droppable da seção.
 */
interface LaneDef {
  key: string;
  titulo: string;
  icon: string;
  /** etapas que caem nesta lane (null/sem etapa → 'fila'). */
  etapas: (EtapaPipeline | null)[];
  /** destaque: lane que pede ação humana. */
  accent?: 'blue' | 'violet';
}

const LANES: LaneDef[] = [
  { key: 'fila',      titulo: 'Fila',        icon: '⏸️', etapas: [null, 'fila'], accent: 'blue' },
  { key: 'preparo',   titulo: 'Preparo',     icon: '⚙️', etapas: ['preparo', 'mapa'], accent: 'blue' },
  // Refinamento removido (UX 2026-06-08): card com "Refinar / Finalizar preparo ▶" fica em Preparo.
  // Só ao clicar "Finalizar preparo ▶" (→ 'redigindo') o card migra para Redação.
  { key: 'redigindo', titulo: 'Redação',     icon: '✍️', etapas: ['mapa_aprovado', 'redigindo', 'revisao', 'concluido'], accent: 'blue' },
];

const ACCENT: Record<string, string> = {
  blue:   'border-blue-400/70 bg-blue-50/40 dark:bg-blue-900/10',
  violet: 'border-violet-400/70 bg-violet-50/40 dark:bg-violet-900/10',
};

interface Props {
  coluna: ColunaCards;                  // a coluna 'em_andamento' do backend
  onCardClick: (id: number) => void;
  dropOk?: boolean;
  dropBloqueado?: boolean;
  draggedCard?: PrazoCard | null;
}

function canDropOnLane(draggedCard: PrazoCard | null, targetLaneKey: string): boolean {
  if (!draggedCard) return false;
  
  // Se o card está na triagem (nao_feitos), pode ser arrastado para qualquer lane em andamento para "Assumir"
  if (draggedCard.coluna === 'nao_feitos') {
    return true;
  }
  
  // Se o card está em andamento (em_andamento), só permitimos regressão de 1 etapa:
  if (draggedCard.coluna === 'em_andamento') {
    const currentEtapa = draggedCard.etapa_pipeline ?? 'fila';
    
    if (currentEtapa === 'preparo' && targetLaneKey === 'fila') {
      return true;
    }
    // Sem coluna Refinamento: a regressão de Redação volta direto para Preparo.
    if (['mapa', 'mapa_aprovado', 'redigindo', 'revisao', 'concluido'].includes(currentEtapa) && targetLaneKey === 'preparo') {
      return true;
    }
  }
  
  return false;
}

interface LaneProps {
  laneKey: string;
  titulo: string;
  icon: string;
  cards: PrazoCard[];
  accent: string;
  onCardClick: (id: number) => void;
  draggable: boolean;
  draggedCard: PrazoCard | null;
}

function LaneKanban({
  laneKey,
  titulo,
  icon,
  cards,
  accent,
  onCardClick,
  draggable,
  draggedCard,
}: LaneProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `lane-${laneKey}`,
  });

  const dropOk = canDropOnLane(draggedCard, laneKey);
  const dropBlocked = draggedCard !== null && !dropOk && (draggedCard.coluna !== 'em_andamento' || draggedCard.etapa_pipeline !== laneKey);

  const highlightClass =
    isOver && dropBlocked
      ? 'ring-2 ring-red-400 dark:ring-red-700 bg-red-500/5'
      : isOver && dropOk
      ? 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-500/5 shadow-md scale-[1.01] transition-all'
      : dropOk
      ? 'ring-1 ring-dashed ring-blue-300 dark:ring-blue-700/50'
      : '';

  return (
    <div
      ref={setNodeRef}
      className={`w-full rounded-lg border ${accent} flex flex-col transition-all duration-200 ${highlightClass}`}
    >
      <div className="px-2.5 py-2 flex items-center justify-between border-b border-slate-200/50 dark:border-slate-800/50">
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
          {icon} {titulo}
        </span>
        <span className="text-[10px] font-mono text-slate-400">{cards.length}</span>
      </div>
      <div className="col-scroll p-1.5 space-y-2 min-h-[80px]">
        {cards.length > 0 ? (
          cards.map((c) => (
            <CardPrazo key={c.id_prazo} card={c} onClick={() => onCardClick(c.id_prazo)} draggable={draggable} />
          ))
        ) : (
          <p className="text-[10px] text-slate-300 dark:text-slate-600 italic px-1.5 py-1">—</p>
        )}
      </div>
    </div>
  );
}

function laneDoCard(c: PrazoCard): string {
  const e = c.etapa_pipeline;
  const found = LANES.find((l) => l.etapas.includes(e ?? null));
  return found ? found.key : 'fila';
}

export function EsteiraAndamento({ coluna, onCardClick, dropOk = false, dropBloqueado = false, draggedCard = null }: Props) {
  const { setNodeRef, isOver } = useDroppable({ id: 'col-em_andamento' });
  const { data: me } = useMe();
  const draggable = me?.papel === 'admin';

  const porLane: Record<string, PrazoCard[]> = {};
  for (const l of LANES) porLane[l.key] = [];
  for (const c of coluna.cards) porLane[laneDoCard(c)].push(c);
  // ordena cada lane por urgência (vence antes primeiro), desempatando pelo nº do processo
  for (const k of Object.keys(porLane)) {
    porLane[k].sort((a, b) => (a.dias_ate_vencimento - b.dias_ate_vencimento)
      || a.numero_processo.localeCompare(b.numero_processo));
  }

  const dragFeedback =
    isOver && dropBloqueado ? 'ring-2 ring-red-400 dark:ring-red-700'
    : isOver && dropOk ? 'ring-4 ring-blue-500 dark:ring-blue-400 shadow-lg'
    : dropOk ? 'ring-1 ring-blue-200 dark:ring-blue-800'
    : '';

  return (
    <section
      ref={setNodeRef}
      className={`bg-gradient-to-b from-blue-500/5 to-blue-500/10 dark:from-blue-950/20 dark:to-blue-950/10 rounded-xl border-2 border-blue-400/80 dark:border-blue-700/80 shadow-md shadow-blue-500/5 transition ${dragFeedback}`}
    >
      <header className="px-4 py-3 border-b border-blue-200 dark:border-blue-800/80 flex items-center justify-between bg-blue-100/30 dark:bg-blue-950/30 rounded-t-xl">
        <div className="flex items-center gap-2">
          <h2 className="font-display font-bold text-blue-800 dark:text-blue-200 uppercase tracking-wide text-sm">
            📝 Em andamento
          </h2>
        </div>
        <span className="px-2 py-0.5 bg-blue-600 dark:bg-blue-500 text-white rounded-md text-xs font-bold font-mono">
          {coluna.total}
        </span>
      </header>

      {/* Cards da esteira "Em andamento": TODOS os papéis que veem o quadro
          enxergam os processos (req do usuário). Os botões que disparam o
          pipeline ficam escondidos para o colaborador (gate no CardPrazo) e o
          backend mantém a trava real (require_capacidade / _checar_permissao). */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-2">
        {LANES.map((l) => {
          const cards = porLane[l.key];
          const accent = l.accent ? ACCENT[l.accent] : 'border-slate-200/60 dark:border-slate-800/60';
          return (
            <LaneKanban
              key={l.key}
              laneKey={l.key}
              titulo={l.titulo}
              icon={l.icon}
              cards={cards}
              accent={accent}
              onCardClick={onCardClick}
              draggable={draggable}
              draggedCard={draggedCard}
            />
          );
        })}
      </div>
    </section>
  );
}
